import {Tooltip} from '@dropbox/dig-components/tooltips';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import type {DocumentRedactionInlineContent} from 'client';
import {
  TOGGLE_REDACTION_V2_EDITOR_COMMAND,
  useRedactionV2Entry,
} from 'components/DSYS/editor/plugins/redactionV2/RedactionV2Context';
import {
  $applyNodeReplacement,
  $createTextNode,
  $parseSerializedNode,
  DecoratorNode,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  SerializedTextNode,
  Spread,
} from 'lexical';
import {ReactNode} from 'react';

import styles from './RedactionV2Node.module.css';

export const REDACTION_V2_MASK_SIZES = ['short', 'medium', 'long'] as const;
export type RedactionV2MaskSize = (typeof REDACTION_V2_MASK_SIZES)[number];
export const REDACTION_V2_INLINE_CONTENT_KIND = 'lexical_inline_fragment_v1';
export type RedactionV2TextContentNode = Record<string, unknown> &
  SerializedTextNode & {
    type: 'text';
    text: string;
    format: number;
    detail: number;
    mode: 'normal';
    style: string;
    version: number;
  };
export type RedactionV2InlineContent = {
  kind: typeof REDACTION_V2_INLINE_CONTENT_KIND;
  nodes: RedactionV2TextContentNode[];
};

export type SerializedRedactionV2Node = Spread<
  {
    type: 'redaction-v2';
    version: 1;
    redactionId: string;
    maskSize: RedactionV2MaskSize;
  },
  SerializedLexicalNode
>;

export const getRedactionV2MaskSize = (characterCount: number): RedactionV2MaskSize => {
  const normalizedCount =
    Number.isFinite(characterCount) && characterCount > 0 ? Math.floor(characterCount) : 0;

  if (normalizedCount <= 12) {
    return 'short';
  }
  if (normalizedCount <= 40) {
    return 'medium';
  }
  return 'long';
};

export const normalizeRedactionV2MaskSize = (maskSize: unknown): RedactionV2MaskSize => {
  return REDACTION_V2_MASK_SIZES.includes(maskSize as RedactionV2MaskSize)
    ? (maskSize as RedactionV2MaskSize)
    : 'medium';
};

const TEXT_FORMAT_BOLD = 1;
const TEXT_FORMAT_ITALIC = 2;
const TEXT_FORMAT_STRIKETHROUGH = 4;
const TEXT_FORMAT_UNDERLINE = 8;
const TEXT_FORMAT_CODE = 16;

const isRedactionV2TextContentNode = (node: unknown): node is RedactionV2TextContentNode => {
  if (!node || typeof node !== 'object') {
    return false;
  }

  const value = node as Partial<RedactionV2TextContentNode>;
  return (
    value.type === 'text' &&
    typeof value.text === 'string' &&
    typeof value.format === 'number' &&
    typeof value.detail === 'number' &&
    value.mode === 'normal' &&
    typeof value.style === 'string' &&
    typeof value.version === 'number'
  );
};

export const isRedactionV2InlineContent = (
  content: DocumentRedactionInlineContent | null | undefined
): content is RedactionV2InlineContent =>
  content?.kind === REDACTION_V2_INLINE_CONTENT_KIND &&
  Array.isArray(content.nodes) &&
  content.nodes.every(isRedactionV2TextContentNode);

export const createRedactionV2InlineContentFromText = (text: string): RedactionV2InlineContent => ({
  kind: REDACTION_V2_INLINE_CONTENT_KIND,
  nodes:
    text.length > 0
      ? [
          {
            type: 'text',
            text,
            format: 0,
            detail: 0,
            mode: 'normal',
            style: '',
            version: 1,
          },
        ]
      : [],
});

export const getRedactionV2TextFromContent = (
  content: DocumentRedactionInlineContent | null | undefined
): string =>
  isRedactionV2InlineContent(content) ? content.nodes.map((node) => node.text).join('') : '';

export const getRedactionV2TextFormatClassName = (format: number): string => {
  const isUnderline = Boolean(format & TEXT_FORMAT_UNDERLINE);
  const isStrikethrough = Boolean(format & TEXT_FORMAT_STRIKETHROUGH);
  return [
    styles.formattedText,
    format & TEXT_FORMAT_BOLD ? styles.bold : null,
    format & TEXT_FORMAT_ITALIC ? styles.italic : null,
    format & TEXT_FORMAT_CODE ? styles.code : null,
    isUnderline && isStrikethrough
      ? styles.underlineStrikethrough
      : isUnderline
        ? styles.underline
        : isStrikethrough
          ? styles.strikethrough
          : null,
  ]
    .filter(Boolean)
    .join(' ');
};

export const $createRedactionV2ContentNodes = (
  content: DocumentRedactionInlineContent | null | undefined,
  fallbackText?: string | null
): LexicalNode[] => {
  const nodes: LexicalNode[] = [];

  if (isRedactionV2InlineContent(content)) {
    content.nodes.forEach((serializedNode) => {
      try {
        nodes.push($parseSerializedNode(serializedNode));
      } catch {
        const textNode = $createTextNode(serializedNode.text);
        textNode.setFormat(serializedNode.format);
        nodes.push(textNode);
      }
    });
  }

  if (nodes.length === 0 && fallbackText) {
    nodes.push($createTextNode(fallbackText));
  }

  return nodes;
};

export const $replaceRedactionV2NodeWithContent = (
  node: RedactionV2Node,
  content: DocumentRedactionInlineContent | null | undefined,
  fallbackText?: string | null
): LexicalNode[] => {
  const restoredNodes = $createRedactionV2ContentNodes(content, fallbackText);
  if (restoredNodes.length === 0) {
    return [];
  }

  restoredNodes.forEach((restoredNode) => {
    node.insertBefore(restoredNode);
  });
  node.remove();
  return restoredNodes;
};

const renderFormattedText = (node: RedactionV2TextContentNode, key: string): ReactNode => {
  return (
    <span key={key} className={getRedactionV2TextFormatClassName(node.format)}>
      {node.text}
    </span>
  );
};

const RedactionV2Placeholder = ({
  nodeKey,
  redactionId,
  maskSize,
}: {
  nodeKey: NodeKey;
  redactionId: string;
  maskSize: RedactionV2MaskSize;
}) => {
  const [editor] = useLexicalComposerContext();
  const {data: redaction} = useRedactionV2Entry(redactionId);
  const content = isRedactionV2InlineContent(redaction?.content) ? redaction.content : null;
  const canView = Boolean(redaction?.can_view && (content || redaction.text));

  const openRedactionEditor = () => {
    if (!canView) {
      return;
    }
    editor.dispatchCommand(TOGGLE_REDACTION_V2_EDITOR_COMMAND, {nodeKey, redactionId});
  };

  if (canView) {
    return (
      <span
        aria-label="Redacted content"
        className={`${styles.content} ${styles.authorizedContent} ${styles.interactive}`}
        data-redaction-v2-id={redactionId}
        data-redaction-v2-mask-size={maskSize}
        onClick={openRedactionEditor}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          event.preventDefault();
          openRedactionEditor();
        }}
        role="button"
        tabIndex={0}
      >
        <span className={styles.authorizedText}>
          {content
            ? content.nodes.map((node, index) =>
                renderFormattedText(node, `${redactionId}-${index}`)
              )
            : redaction?.text}
        </span>
      </span>
    );
  }

  return (
    <Tooltip title="Redacted" openDelay={500} placement="top">
      <span
        aria-label="Redacted content"
        className={`${styles.content} ${styles.placeholder} ${styles[maskSize]}`}
        data-redaction-v2-id={redactionId}
        data-redaction-v2-placeholder="true"
        data-redaction-v2-mask-size={maskSize}
        role="img"
      />
    </Tooltip>
  );
};

export class RedactionV2Node extends DecoratorNode<ReactNode> {
  __redactionId: string;
  __maskSize: RedactionV2MaskSize;

  static getType(): string {
    return 'redaction-v2';
  }

  static clone(node: RedactionV2Node): RedactionV2Node {
    return new RedactionV2Node(node.__redactionId, node.__maskSize, node.__key);
  }

  constructor(redactionId: string, maskSize: RedactionV2MaskSize, key?: NodeKey) {
    super(key);
    this.__redactionId = redactionId;
    this.__maskSize = maskSize;
  }

  static importJSON(serializedNode: SerializedRedactionV2Node): RedactionV2Node {
    return $createRedactionV2Node({
      redactionId: serializedNode.redactionId,
      maskSize: normalizeRedactionV2MaskSize(serializedNode.maskSize),
    });
  }

  exportJSON(): SerializedRedactionV2Node {
    return {
      type: 'redaction-v2',
      version: 1,
      redactionId: this.__redactionId,
      maskSize: this.__maskSize,
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.classList.add(styles.redactionV2Node);
    span.contentEditable = 'false';
    span.setAttribute('data-lexical-type', 'redaction-v2');
    span.setAttribute('data-redaction-v2-id', this.__redactionId);
    span.setAttribute('data-redaction-v2-mask-size', this.__maskSize);
    return span;
  }

  updateDOM(prevNode: RedactionV2Node, dom: HTMLElement): boolean {
    if (prevNode.__redactionId !== this.__redactionId) {
      dom.setAttribute('data-redaction-v2-id', this.__redactionId);
    }
    if (prevNode.__maskSize !== this.__maskSize) {
      dom.setAttribute('data-redaction-v2-mask-size', this.__maskSize);
    }
    return false;
  }

  exportDOM(): DOMExportOutput {
    const span = document.createElement('span');
    span.setAttribute('data-lexical-type', 'redaction-v2');
    span.textContent = '[redacted]';
    return {element: span};
  }

  isInline(): boolean {
    return true;
  }

  canInsertTextBefore(): boolean {
    return true;
  }

  canInsertTextAfter(): boolean {
    return true;
  }

  getTextContent(): string {
    return '';
  }

  getRedactionId(): string {
    const self = this.getLatest();
    return self.__redactionId;
  }

  getMaskSize(): RedactionV2MaskSize {
    const self = this.getLatest();
    return self.__maskSize;
  }

  setMaskSize(maskSize: RedactionV2MaskSize): void {
    const writable = this.getWritable();
    writable.__maskSize = maskSize;
  }

  decorate(): ReactNode {
    return (
      <RedactionV2Placeholder
        nodeKey={this.getKey()}
        redactionId={this.__redactionId}
        maskSize={this.__maskSize}
      />
    );
  }
}

export const $createRedactionV2Node = ({
  redactionId,
  maskSize,
}: {
  redactionId: string;
  maskSize: RedactionV2MaskSize;
}): RedactionV2Node => {
  return $applyNodeReplacement(new RedactionV2Node(redactionId, maskSize));
};

export const $isRedactionV2Node = (
  node: LexicalNode | null | undefined
): node is RedactionV2Node => {
  return node instanceof RedactionV2Node;
};
